/**
 * 제안 카드 요청 — 설계 v7 §4-3·§7.
 *
 * 핵심은 **반영 조건**이다. 응답이 도착하면 저장소를 다시 읽어(React 상태가 아니라) 요청 당시의
 * 세대·신원과 맞춘다. 하나라도 다르면 버린다. 느린 응답이 로그아웃이나 계정 전환 뒤에 도착해
 * 남의 사무소 이력을 화면에 올리는 것을 막는 유일한 방어선이다.
 */
import { authEpoch, getAuthSnapshot } from './authState';

export const PUBLIC_PATH = '/api/onto/proposal/public';
export const AUTH_PATH = '/api/onto/proposal';
export const ADOPTION_PATH = '/api/onto/proposal/adoption';
/** 카드가 놓는 네 신호. 이 밖의 이름은 보내지 않는다 — 집계가 흩어진다. */
export const SIGNALS = ['llm', 'history', 'precedent', 'tariff'];
/** 폐기 사유는 한 이름으로 센다 — 원인별로 나누면 집계가 흩어진다. */
export const STALE = 'stale_response_dropped';

/** 세션 표지 키. 검색어·상세·계정·사무소가 모두 들어간다(§4-3). */
export function sessionKey(qHash, detailsHash, snapshot) {
  const userId = snapshot && snapshot.userId ? snapshot.userId : 'anon';
  const groupId = snapshot && snapshot.groupId ? snapshot.groupId : '-';
  return `loginhs:proposal:${userId}:${groupId}:${qHash}:${detailsHash}`;
}

/** 로그아웃·계정 변경 때 이 접두의 표지를 전부 지운다. */
export function clearSessionKeys(storage = window.sessionStorage) {
  try {
    const doomed = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key && key.startsWith('loginhs:proposal:')) doomed.push(key);
    }
    doomed.forEach((key) => storage.removeItem(key));
  } catch (error) {
    /* 저장소 접근 실패는 무시한다 */
  }
}

function sameIdentity(a, b) {
  if (!a || !b) return false;
  return a.userId === b.userId && a.groupId === b.groupId && a.jti === b.jti;
}

/**
 * 응답을 반영해도 되는가 — 네 값을 맞춘다.
 *
 * @param requestEpoch 요청 당시의 세대
 * @param requestSnapshot 요청 당시의 신원(비로그인은 null)
 * @param principal 서버가 검증해 되돌려준 신원(공개 경로는 없음)
 */
export function acceptResponse(requestEpoch, requestSnapshot, principal) {
  if (authEpoch() !== requestEpoch) return false;
  const current = getAuthSnapshot();
  if (!requestSnapshot) {
    // 비로그인 요청이었다. 그 사이에 로그인했으면 이 응답은 이력이 빠진 낡은 것이다.
    return current === null && !principal;
  }
  if (!current) return false;               // 토큰이 사라졌다(로그아웃)
  if (!sameIdentity(current, requestSnapshot)) return false;
  if (!principal) return false;             // 인증 요청인데 서버가 신원을 echo 하지 않았다
  return sameIdentity(current, principal);  // 서버가 본 신원과도 같아야 한다
}

/**
 * 카드 한 번의 요청.
 *
 * @returns {{status:'ok', data:object}|{status:'stale'}|{status:'aborted'}|{status:'error', error:Error}}
 */
/**
 * 상품 상세를 **문자열로** 보낸다.
 *
 * 서버는 `isTextual()` 일 때만 읽는다. 화면이 넘기는 `productDetails` 는
 * `{material, intendedUse, construction, articleRole}` 객체라, 그대로 실으면 서버가 늘 빈
 * 문자열로 읽어 **프롬프트와 캐시 키에서 상세가 통째로 빠졌다**(코드 리뷰 v9).
 *
 * 키 순서를 고정해 같은 상세가 같은 캐시 키를 만들게 한다.
 */
export function detailsText(details) {
  if (!details) return '';
  const joined = typeof details === 'string' ? details.trim()
    : typeof details !== 'object' ? String(details).trim()
      : Object.keys(details)
        .sort()
        .map((key) => [key, String(details[key] == null ? '' : details[key]).trim()])
        .filter(([, value]) => value)
        .map(([key, value]) => `${key}: ${value}`)
        .join(' · ');
  return clampBytes(joined, MAX_DETAILS_BYTES);
}

/** 서버 상한(`ProposalController.MAX_DETAILS_BYTES`). 넘으면 400 이라 카드 전체가 오류가 된다. */
const MAX_DETAILS_BYTES = 2048;

/**
 * UTF-8 바이트 기준으로 자른다. 글자 수로 자르면 한글이 상한을 넘긴다.
 *
 * 상세가 길다고 카드가 통째로 죽어서는 안 된다 — 상세는 힌트를 좁히는 보조 입력이다
 * (코드 리뷰 v10). 자른 자리는 표시하지 않는다. 서버는 이 값을 프롬프트·캐시 키에만 쓴다.
 *
 * `TextEncoder` 를 쓰지 않는다. 시험 환경(jsdom)에 없어서 카드 계약 시험이 통째로 죽었다.
 * 코드포인트마다 바이트 수를 더하는 것으로 충분하다 — 대리쌍도 한 번에 센다.
 */
export function clampBytes(text, limit) {
  let bytes = 0;
  let cut = 0;
  for (const character of String(text)) {
    const code = character.codePointAt(0);
    const size = code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4;
    if (bytes + size > limit) return text.slice(0, cut);
    bytes += size;
    cut += character.length;          // 대리쌍은 2
  }
  return text;
}

export async function fetchProposal({ query, productDetails, signal, fetchImpl = fetch }) {
  const snapshot = getAuthSnapshot();
  const requestEpoch = authEpoch();
  const path = snapshot ? AUTH_PATH : PUBLIC_PATH;
  const headers = { 'Content-Type': 'application/json' };
  if (snapshot) headers.Authorization = `Bearer ${snapshot.token}`;

  let response;
  try {
    response = await fetchImpl(path, {
      method: 'POST',
      headers,
      signal,
      body: JSON.stringify({ q: query, product_details: detailsText(productDetails) }),
    });
  } catch (error) {
    if (error && error.name === 'AbortError') return { status: 'aborted' };
    return { status: 'error', error };
  }
  if (!response.ok) {
    return { status: 'error', error: new Error(`proposal ${response.status}`) };
  }
  // **본문 파싱을 감싼다.** nginx 에 경로가 없으면 Node 가 200 HTML 을 줄 수 있고, 그때
  // `response.json()` 이 try 밖에서 던져 카드가 "신호를 모으는 중입니다." 에 영원히 멈췄다
  // (코드 리뷰 v10 — 무역통계부호 때 실제로 있었던 사고 모양이다).
  let data;
  try {
    data = await response.json();
  } catch (error) {
    return { status: 'error', error };
  }

  // 반영 직전에 저장소를 다시 읽는다. 여기까지 오는 동안 계정이 바뀌었을 수 있다.
  if (!acceptResponse(requestEpoch, snapshot, data && data.principal)) {
    return { status: 'stale', reason: STALE };
  }
  return { status: 'ok', data };
}

/**
 * 채택 기록 — 구현지시 §5. **이 표가 이 기능의 유일한 성능 지표다.**
 *
 * 보내는 것은 서버가 준 요청 해시와 신호 이름뿐이다. 품명·계정은 싣지 않는다.
 * 기록이 실패해도 사용자 동작을 막지 않는다 — 강조는 이미 일어났다.
 */
export async function recordAdoption(requestHash, signal, fetchImpl = fetch) {
  if (!requestHash || !SIGNALS.includes(signal)) return false;
  try {
    const response = await fetchImpl(ADOPTION_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ request_hash: requestHash, signal }),
    });
    return response.ok;
  } catch (error) {
    return false;
  }
}

/** 조건값은 대문자 문자열이다. `=== 'TRUE'` 외에는 참으로 보지 않는다(§4-2). */
export function isTrue(condition) {
  return !!condition && condition.value === 'TRUE';
}

export function allConditionsTrue(conditions) {
  if (!conditions) return false;
  return ['lines_ge2', 'decls_ge2', 'single_hsk', 'active', 'not_amended_before']
    .every((name) => isTrue(conditions[name]));
}
